import type * as rtk from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction, Reducer } from '@reduxjs/toolkit';
import { config } from "../config";

// Interfaces
export interface Space {
  _id: { $oid: string };
  space_name: string;
  project_id: string;
  created_at: string;
  last_updated: string;
  space_type: string;
  space_owner: string;
  metadata: any;
}

export interface SpacesState {
  spaces: Space[];
  activeSpace: string;
  loading: boolean;
  error: string | null;
}

interface ServerError {
  status: number;
  short_message: string;
  long_message: string;
  data: Record<string, unknown>;
}

// Initial State
const initialState: SpacesState = {
  spaces: [],
  activeSpace: '',
  loading: false,
  error: null,
};

// Backend URL function
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.docloo.co.uk';
  } else {
    return 'http://192.168.53:3011';
  }
};

const backendUrl = getBaseUrl();

// Thunks
// Fetch spaces
export const fetchSpaces = createAsyncThunk(
  'spaces/fetchSpaces',
  async (
    { token, projectId, forceFetch = false }: { token: string; projectId: string; forceFetch?: boolean },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as { spaces: SpacesState };
    const existingSpaces = state.spaces.spaces;
console.log("existingSpaces", existingSpaces);
    if (existingSpaces.length > 0 && !forceFetch) {
      return existingSpaces;
    }

    try {
      const response = await fetch(`${backendUrl}/spaces/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ project_id: projectId }),
      });

      console.log("response", response);

      if (!response.ok) {
        const errorData: ServerError = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to fetch spaces.');
      }

      const data = await response.json();
      return data.data.spaces as Space[];
    } catch (error) {
      return rejectWithValue(
        error instanceof TypeError ? 'Internet connection error' : (error as Error).message || 'Something went wrong'
      );
    }
  }
);

// Create a new space
export const createSpace = createAsyncThunk(
  'spaces/createSpace',
  async (
    { token, space }: { token: string; space: { name: string; project_id: string; description: string; space_type: string } },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch(`${backendUrl}/spaces/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(space),
      });

      if (!response.ok) {
        const errorData: ServerError = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to create space.');
      }

      const data = await response.json();
      return data.data as Space;
    } catch (error) {
      return rejectWithValue(
        error instanceof TypeError ? 'Internet connection error' : (error as Error).message || 'Something went wrong'
      );
    }
  }
);

// Delete a space
export const deleteSpace = createAsyncThunk(
  'spaces/deleteSpace',
  async ({ token, spaceId, projectId }: { token: string; spaceId: string; projectId: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backendUrl}/spaces/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ space_id: spaceId, project_id: projectId }),
      });

      if (!response.ok) {
        const errorData: ServerError = await response.json();
        return rejectWithValue(errorData.short_message || 'Failed to delete space.');
      }

      return spaceId;
    } catch (error) {
      return rejectWithValue(
        error instanceof TypeError ? 'Internet connection error' : (error as Error).message || 'Something went wrong'
      );
    }
  }
);

// Slice
const spacesSlice = createSlice({
  name: 'spaces',
  initialState,
  reducers: {
    setActiveSpace: (state, action: PayloadAction<string>) => {
      state.activeSpace = action.payload;
      if (typeof window !== 'undefined') {
        localStorage.setItem('activeSpace', action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpaces.fulfilled, (state, action: PayloadAction<Space[]>) => {
        state.loading = false;
        state.spaces = action.payload;
        if (!state.activeSpace && action.payload.length > 0) {
          state.activeSpace = localStorage.getItem('activeSpace') || action.payload[0]._id.$oid;
          if (typeof window !== 'undefined') {
            localStorage.setItem('activeSpace', state.activeSpace);
          }
        }
      })
      .addCase(fetchSpaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSpace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSpace.fulfilled, (state, action: PayloadAction<Space>) => {
        state.loading = false;
        state.spaces.push(action.payload);

        console.log("action.payload", action.payload);
        state.activeSpace = localStorage.getItem('activeSpace') || action.payload._id.$oid;
        if (typeof window !== 'undefined') {
          localStorage.setItem('activeSpace', state.activeSpace);
        }
      })
      .addCase(createSpace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteSpace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSpace.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.spaces = state.spaces.filter((space) => space._id.$oid !== action.payload);
        if (state.activeSpace === action.payload) {
          state.activeSpace = '';
          if (typeof window !== 'undefined') {
            localStorage.removeItem('activeSpace');
          }
        }
      })
      .addCase(deleteSpace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setActiveSpace } = spacesSlice.actions;

// Reducer
const spacesReducer: Reducer<SpacesState> = spacesSlice.reducer;
export default spacesReducer;
